import { useCallback, useMemo, useState } from 'react';

export type Booleans<T extends string> = { [key in T]?: boolean };
export type BooleansFn<T extends string> = {
  on: OnOff<T>;
  off: OnOff<T>;
  toggle: ToggleFn<T>;
  set: SetFn<T>;
};
type OnOff<T extends string> = (key: T) => void;
type SetFn<T extends string> = (key: T, bool: boolean) => void;
type ToggleFn<T extends string> = (key: T) => void;

/**
 * Custom hook for managing multiple boolean flags.
 *
 * @param flags - List of boolean flags.
 * @returns Array containing the current state of flags and control functions.
 *
 * Usage:
 *
 * const [flags, { on, off, toggle }] = useBooleans('loading', 'writing', 'deleting');
 *
 * <Button
 *   label={flags.loading ? 'Loading' : 'Load'}
 *   onClick={() => on('loading')}
 * />
 * <Button
 *   label={flags.writing ? 'Writing' : 'Write'}
 *   onClick={() => off('writing')}
 * />
 * <Button
 *   label={flags.deleting ? 'Deleting' : 'Delete'}
 *   onClick={() => toggle('deleting')}
 * />
 */

const useBooleans = <T extends string>(...flags: T[]): [Booleans<T>, BooleansFn<T>] => {
  // Lazy initialiser: the seed is only used on mount, so it should not be
  // rebuilt on every render.
  const [state, setState] = useState<Booleans<T>>(() => {
    const initial: Booleans<T> = {};
    for (const flag of flags) initial[flag] = false;
    return initial;
  });

  const on = useCallback((key: T) => {
    setState((prev) => ({ ...prev, [key]: true }));
  }, []);

  const off = useCallback((key: T) => {
    setState((prev) => ({ ...prev, [key]: false }));
  }, []);

  const toggle = useCallback((key: T) => {
    setState((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const set = useCallback((key: T, bool: boolean) => {
    setState((prev) => ({ ...prev, [key]: bool }));
  }, []);

  const fn = useMemo(() => ({ on, off, toggle, set }), [on, off, toggle, set]);

  return [state, fn];
};

export default useBooleans;
